import {
  createContext,
  Fragment,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
  FC,
  ReactNode,
  ComponentType,
  ComponentProps,
} from 'react'
import { Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/outline'
import { Spinner } from './Spinner'
import { createPortal } from 'react-dom'
import { noop } from 'lodash'
import { LegacySdkResponseError, ResponseError } from 'api'

export type Key = number | string

interface NotificationOptions {
  key?: Key
}

interface Context {
  clearAllNotifications(): void
  error(title: string, params?: Partial<Omit<NotificationProps, 'variant'>>): Key
  notify(notification: JSX.Element, options?: NotificationOptions): Key
  /** Accepts a legacy or TS SDK response error and creates a notification. */
  notifyApiError(response: ResponseError, description?: string): Promise<Key>
  removeNotification(key: Key): void
  success(title: string, params?: Partial<Omit<NotificationProps, 'variant'>>): Key
  /** @deprecated */
  toast(notification: JSX.Element): void
}

export const NotificationContext = createContext<Context>({
  clearAllNotifications: noop,
  error: noop as Context['error'],
  notify: noop as Context['notify'],
  notifyApiError: noop as Context['notifyApiError'],
  removeNotification: noop,
  success: noop as Context['success'],
  /** @deprecated */
  toast: noop,
})

interface Props {
  children: ReactNode
}

export const NotificationProvider: FC<Props> = ({ children }) => {
  const [notifications, setNotifications] = useState<Record<string, JSX.Element>>({})

  const clearAllNotifications = useCallback<Context['clearAllNotifications']>(
    () => setNotifications({}),
    []
  )

  const notify = useCallback<Context['notify']>((notification, options = {}) => {
    const key = options.key ?? Date.now()

    setNotifications((previous) => ({ ...previous, [key]: notification }))

    return key
  }, [])

  /** Creates a notification from a legacy or TS SDK error response. */
  const notifyApiError = useCallback<Context['notifyApiError']>(
    async (error, description) => {
      try {
        const body = 'error' in error.response ? error.response.body : await error.response.json()

        return notify(<ApiErrorNotification error={{ body }} />)
      } catch {
        return notify(
          <Notification variant="danger" title="An error occurred" description={description} />
        )
      }
    },
    [notify]
  )

  const error = useCallback<Context['error']>(
    (title, params = {}) => {
      return notify(<Notification autoDismiss {...params} title={title} variant="danger" />)
    },
    [notify]
  )

  const success = useCallback<Context['success']>(
    (title, params = {}) => {
      return notify(<Notification autoDismiss {...params} title={title} variant="success" />)
    },
    [notify]
  )

  const removeNotification = useCallback<Context['removeNotification']>((key) => {
    setNotifications((previous) => {
      delete previous[key]

      return { ...previous }
    })
  }, [])
  /**
   * This is here for compatibility to allow direct swapping
   * from @trybeapp/ui.toast to new notifications.
   * @deprecated Use notifications.notify() instead
   */
  const toast = useCallback((element: JSX.Element, position = {}) => notify(element), [notify])

  const context = useMemo(
    () => ({
      notify,
      notifyApiError,
      clearAllNotifications,
      error,
      removeNotification,
      success,
      toast,
    }),
    [clearAllNotifications, error, notify, notifyApiError, removeNotification, success, toast]
  )

  return (
    <NotificationContext.Provider value={context}>
      <>
        {children}

        {createPortal(
          <NotificationArea>
            {Object.entries(notifications).map(([key, notification]) => (
              <Fragment key={key}>{notification}</Fragment>
            ))}
          </NotificationArea>,
          document.getElementsByTagName('body')[0]
        )}
      </>
    </NotificationContext.Provider>
  )
}

export function withNotifications<C extends ComponentType<any>>(
  Component: C
): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <NotificationProvider>
        <Component {...props} />
      </NotificationProvider>
    )
  }
}

interface NotificationAreaProps {
  children: ReactNode
}

export const NotificationArea: FC<NotificationAreaProps> = ({ children }) => (
  <div
    aria-live="assertive"
    className="fixed inset-0 flex pt-20 sm:pt-6 px-4 py-6 pointer-events-none sm:p-6 z-60"
  >
    <div className="w-full flex flex-col items-center space-y-4 sm:items-end">{children}</div>
  </div>
)

export interface ErrorNotificationProps {
  /**
   * Accepts a legacy response error only.
   * @see Context.notifyApiError() to handle TS SDK error responses.
   */
  error?: Pick<LegacySdkResponseError, 'body'>
  defaultDescription?: string
}

export const ApiErrorNotification: FC<ErrorNotificationProps> = ({
  error,
  defaultDescription = 'An error occurred, please try again.',
  ...props
}) => {
  const { message: title, errors = {} } = error?.body ?? {}
  const messages = Object.values(errors).flat()

  const description =
    messages.length > 0 ? 'Please fix the errors below and try again.' : defaultDescription

  return (
    <Notification
      variant="danger"
      title={title || 'An error occurred'}
      listMessages={messages}
      description={description}
      {...props}
    />
  )
}

interface NotificationProps {
  title: string
  description?: string
  variant: 'success' | 'info' | 'danger' | 'warning' | 'pending'
  autoDismiss?: boolean
  listMessages?: string[]
  hideClose?: boolean
}

export const Notification: FC<NotificationProps> = ({
  title,
  description,
  variant,
  autoDismiss = false,
  listMessages = [],
  hideClose = false,
}) => {
  const [show, setShow] = useState(true)

  const [textColor, backgroundColor] = useMemo(() => {
    switch (variant) {
      default:
      case 'info':
        return ['text-blue-400', 'bg-blue-200']
      case 'success':
        return ['text-green-400', 'bg-green-200']
      case 'warning':
        return ['text-yellow-400', 'bg-yellow-200']
      case 'danger':
        return ['text-red-400', 'bg-red-200']
    }
  }, [variant])

  const icon = useMemo(() => {
    switch (variant) {
      default:
      case 'info':
        return InformationCircleIcon
      case 'pending':
        return () => <Spinner variant="info" />
      case 'success':
        return CheckCircleIcon
      case 'danger':
        return XCircleIcon
      case 'warning':
        return ExclamationCircleIcon
    }
  }, [variant])

  useEffect(() => {
    if (!autoDismiss) return

    const timer = setTimeout(() => setShow(false), 6000)

    return () => clearTimeout(timer)
  }, [autoDismiss])

  return (
    <Transition
      show={show}
      as={Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="max-w-sm w-full bg-white shadow-lg rounded-xl pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start">
            {icon && (
              <div className="flex-shrink-0">
                {icon({
                  className: `h-6 w-6 ${textColor}`,
                  'aria-hidden': true,
                })}
              </div>
            )}

            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900 break-words">{title}</p>
              {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
              {listMessages.length > 0 && (
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  {listMessages.map((message, i) => (
                    <li className="text-sm text-gray-500" key={i}>
                      {message}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="ml-4 flex-shrink-0 flex">
              {hideClose === false && (
                <button
                  aria-label="Close"
                  className="bg-white rounded-xl inline-flex text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet"
                  onClick={() => setShow(false)}
                >
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>
        </div>

        {autoDismiss && (
          <div className="mt-1 w-full h-1">
            <div className={`h-1 ${backgroundColor} animate-dismiss-indicator`} />
          </div>
        )}
      </div>
    </Transition>
  )
}

interface ToastProps {
  children: ReactNode[]
  variant: NotificationProps['variant']
}

/**
 * @deprecated
 */
export const Toast: FC<ToastProps> = ({ variant, children }) => {
  let newVariant = variant

  // variant 'error' is now called 'danger'
  // @ts-ignore
  if (variant === 'error') {
    newVariant = 'danger'
  }

  // The title is in the children as an element called Toast.Title.
  // We need to move this to the title prop
  // Everything else in the children is the description
  const [titleComponent, ...description] = children
  // @ts-ignore
  const title = titleComponent.props.children

  return (
    <Notification
      variant={newVariant}
      title={title.toString()}
      description={description.toString()}
      autoDismiss
    />
  )
}

/**
 * @deprecated
 */
// @ts-ignore
Toast.Title = ({ children }) => children

export const useNotificationContext = (): Context => useContext(NotificationContext)

/**
 * This is here for compatibility to allow direct swapping
 * from @trybeapp/ui.toast to new notifications.
 * @deprecated Use useNotificationContext instead
 */
export const useToast = (): Context['toast'] => {
  const context = useContext(NotificationContext)

  return context.toast
}
